import React, {useEffect, useState} from 'react';
import {toast} from "react-toastify";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import DeleteSharpIcon from "@mui/icons-material/DeleteSharp";
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import {useUserContext} from "../../../contexts/UserContext";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import {useDebounce} from "@uidotdev/usehooks";
import Stack from "@mui/material/Stack";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SendIcon from '@mui/icons-material/Send';
import {formatDate} from "../../../utils/utils";
import {getQuotesDataAndPDFs} from "../../Email/fetchData/getQuotesDataAndPDFs";
import {EmailModal} from "../../Email/EmailModal";
import {ConvertToClientModal} from "./ConvertToClientModal";
import {DealModal} from "../../PricingListPage/DealModal";
import {CRMListDealsModal} from "../CRMListDealsPage/CRMListDealsModal";
import {CustomCRMItemRowEntry} from "../CustomCRMItemRowEntry";
import {useFetchHook} from "../../../hooks/useFetchHook";
import fetcher from "../../../fetcher";
import {useLeadsStore} from "../../../stores/leadsStore";
import {useShallow} from "zustand/react/shallow";
import {useCRMStore} from "../../../stores/crmStore";
import {useModalsStore} from "../../../stores/modalsStore";

const getInitialState = (lead) => ({...lead})

export const LeadItem = ( { lead } ) => {
    const [leadState, setLeadState] = useState(getInitialState(lead))
    const debouncedLeadState = useDebounce(leadState, 300)

    const { profileState } = useUserContext()

    const [createClientResponse, createClient] = useFetchHook(fetcher.createClient)
    const [updateLeadResponse, updateLead] = useFetchHook(fetcher.updateLead)
    const [deleteLeadResponse, deleteLead] = useFetchHook(fetcher.deleteLead)

    const {
        openModal,
        closeModalById,
        updateModalById,
        findModalById,
    } = useModalsStore(
        useShallow((state) => ({
            openModal: state.openModal,
            closeModalById: state.closeModalById,
            updateModalById: state.updateModalById,
            findModalById: state.findModalById,
        }))
    )

    const [isFocused, setIsFocused] = useState(false);
    const [isEmailFocused, setIsEmailFocused] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const removeDeletedLeadFromList = useLeadsStore((state) => state.removeDeletedLeadFromList)
    const { currentMonth, setIsLoading } = useCRMStore(
        useShallow((state) => ({
            currentMonth: state.currentMonth,
            setIsLoading: state.setIsLoading,
        }))
    )

    useEffect(function updateLeadstListAfterDelete() {
        if (deleteLeadResponse?.data?.data?.id) {
            removeDeletedLeadFromList(deleteLeadResponse?.data?.data?.id)
        }
    }, [deleteLeadResponse])

    const handleOnFocus = () => {
        setIsFocused(true);
    }

    const handleBlur = () => {
        setIsFocused(false);
    }

    const handleEmailFocus = () => {
        setIsEmailFocused(true);
    }

    const handleEmailBlur = () => {
        setIsEmailFocused(false);
    }

    const handleActionClick = (event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget);
    };

    const handleActionClose = () => {
        setAnchorEl(null);
    };

    const updateLeadItem = () => {
        setIsLoading(true)
        return updateLead(lead.id, leadState).then((data) => {
            setIsLoading(false)
        })
    }

    const deleteLeadItem = () => {
        return deleteLead(lead.id).then((data) => {
            toast.success("Successfully deleted lead.")
        })
    }

    const convertClient = (data, shouldDeleteLead) => {
        return createClient(data).then((data) => {
            toast.success("Successfully created client.")
            closeModalById("ConvertToClientModal")
            if (shouldDeleteLead) {
                deleteLeadItem()
            }
        })
    }

    const setStateWithUpdate = (newState) => {
        setLeadState({...newState, _should_update: true})
    }

    const setStateWithoutUpdate = (newState) => {
        setLeadState({...newState, _should_update: false})
    }

    const setLeadQuotes = (toggledDealId) => {
        let newDealIds = structuredClone(leadState.deals)
        if (newDealIds.includes(toggledDealId)) {
            newDealIds = newDealIds.filter((dealId) => dealId !== toggledDealId)
        } else {
            newDealIds = [...newDealIds, toggledDealId]
        }

        const newState = {...leadState, deals: newDealIds}
        return updateLead(lead.id, newState).then((data) => {
            const newReturnedDeals = data?.data?.deals
            setStateWithoutUpdate({...leadState, deals: newReturnedDeals})
            setIsLoading(false)
            return newReturnedDeals
        })
    }

    useDidMountEffect(function updateLeadItemAfterDebounce(){
        if (debouncedLeadState._should_update === true && !isEmailFocused) {
            updateLeadItem()
        }
    }, [debouncedLeadState, isEmailFocused])


    useDidMountEffect(function setLeadItemStateOnUpdateResponse(){
        if (updateLeadResponse?.data?.data && !updateLeadResponse.loading && !updateLeadResponse.error) {
            const newLeadData = updateLeadResponse?.data?.data
            if (newLeadData.id === lead.id) {
                const newDeals = newLeadData.deals
                setStateWithoutUpdate({...leadState, deals: newDeals})

                if (findModalById("CRMListDealsModal")) {
                    updateModalById("CRMListDealsModal", {
                        element: CRMListDealsModal,
                        id: "CRMListDealsModal",
                        props: {
                            initialSelectedIds: newDeals,
                            onSelect: (dealId) => setLeadQuotes(dealId)
                        }
                    })
                }
            }
        }

    }, [updateLeadResponse])

    if (!leadState) return null;

    const columns = profileState.lead_columns
    const sortedColumns = Object.keys(columns).sort((a, b) => a.order - b.order)

    const isCurrentMonth = currentMonth === new Date().getMonth()

    let rolloverMonth = new Date()
    if (isCurrentMonth) {
        rolloverMonth = new Date(rolloverMonth.getFullYear(), rolloverMonth.getMonth() + 1, 1)
    }

    return (
        <tr className={'LeadItem'}>
            <td className={'LeadItem-td'} key='Name'>
                <input
                    onFocus={handleOnFocus}
                    onBlur={handleBlur}
                    type="text"
                    onChange={(e) => {
                        setStateWithUpdate({...leadState, name: e.target.value})
                    }}
                    value={leadState.name}
                />
            </td>
            <td className={'LeadItem-td'} key='Phone'>
                <input
                    onFocus={handleOnFocus}
                    onBlur={handleBlur}
                    type="text"
                    onChange={(e) => {
                        setStateWithUpdate({...leadState, phone_number: e.target.value})
                    }}
                    value={leadState.phone_number}
                />
            </td>
            <td className={'LeadItem-td'} key='Email'>
                <input
                    onFocus={(e) => {
                        handleOnFocus(e)
                        handleEmailFocus()
                    }}
                    onBlur={(e) => {
                        handleBlur(e)
                        handleEmailBlur(e)
                    }}
                    type="text"
                    onChange={(e) => {
                        setStateWithUpdate({...leadState, email: e.target.value})
                    }}
                    value={leadState.email}
                />
            </td>
            {
                sortedColumns.map(columnID => {
                    return (
                        <CustomCRMItemRowEntry
                            key={columnID}
                            handleBlur={handleBlur}
                            handleOnFocus={handleOnFocus}
                            columnID={columnID}
                            columns={columns}
                            state={leadState}
                            setStateWithUpdate={setStateWithUpdate}
                        />
                    )
                })
            }
            <td
                className={'LeadItem-td'}
            >
                <Stack
                    direction="row"
                    justifyContent="space-around"
                    alignItems="center"
                    className={'input-group'}
                >
                    <div
                        className={'LeadItem-td_quotes'}
                        onClick={() => {
                            openModal({
                                element: CRMListDealsModal,
                                id: "CRMListDealsModal",
                                props: {
                                    initialSelectedIds: leadState.deals,
                                    onSelect: (dealId) => setLeadQuotes(dealId),
                                }
                            })
                        }}
                    >
                        {leadState.deals.length} Quote{leadState.deals.length === 1 ? "" : "s"}
                    </div> {" "} | {" "}
                    <div
                        className={'ClientItem-td_deal'}
                        onClick={() => {
                            window.onLeadCreate = function(createData) {
                                toast.success("Successfully created quote for lead.")
                                openModal({
                                    element: DealModal,
                                    id: "DealModal",
                                    props: {id: createData.id}
                                })
                                setStateWithUpdate({...leadState, deals: [...leadState.deals, createData.id]})
                            }
                            window.open(`/calculate?lead_id=${lead.id}`)
                        }}
                    >
                      +
                    </div>
                </Stack>
            </td>

            <td className={'LeadItem-td'}>
                <MoreHorizIcon
                    onClick={handleActionClick}
                    className={'CRM-action-button'}
                />
                <Popover
                    onClick={(e) => e.stopPropagation()}
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleActionClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        horizontal: 'right',
                        vertical: 'top'
                    }}
                >
                    <Typography
                        sx={{ p: 2 }}
                        className={'CRM__action-item'}
                        onClick={() => {
                            openModal({
                                element: ConvertToClientModal,
                                id: "ConvertToClientModal",
                                props: {
                                    lead: leadState,
                                    convertClient: convertClient,
                                    columns: profileState.client_columns
                                }
                            })
                        }}
                    >

                        <SwitchAccountIcon />
                        Convert to Client
                    </Typography>
                    <Typography
                        sx={{ p: 2 }}
                        className={'CRM__action-item'}
                        onClick={() => {
                            return updateLead(lead.id, {...leadState, month_added: formatDate(rolloverMonth)}).then((data) => {
                                toast.success("Success.")
                            })
                        }}
                    >

                        <CalendarMonthIcon />
                        Rollover to {rolloverMonth.toLocaleString('default', { month: 'long' })}
                    </Typography>
                    <Typography
                        sx={{ p: 2 }}
                        className={'CRM__action-item'}
                        onClick={() => {
                            const recipient = leadState.name || leadState.email
                            const companyName = profileState.account_name
                            openModal({
                                element: EmailModal,
                                id: "EmailModal",
                                props: {
                                    header: `Send Quotes to ${leadState.email}`,
                                    getContent: () => getQuotesDataAndPDFs(leadState.deals, recipient, companyName),
                                    to: leadState.email,
                                    from: profileState.user_email,
                                    subject: `Lease Quotes from ${profileState.account_name}`,
                                }
                            })
                        }}
                    >

                        <SendIcon />
                        Send Quotes
                    </Typography>
                    <Typography
                        sx={{ p: 2 }}
                        className={'CRM__action-item'}
                        onClick={deleteLeadItem}
                    >

                        <DeleteSharpIcon />
                        Delete Lead
                    </Typography>
                </Popover>
            </td>
        </tr>
    )
}